import { FC, useEffect, useState } from 'react';

import {
  getShopOrderCashReceiptCancelPending,
  getShopOrderCashReceiptPending,
} from '@shared/api/shopOrder/shopOrder.controller';
import { shopOrderSellerRequestPendingCountUrl } from '@shared/api/shopOrder/shopOrder.hook';
import { IconFormer } from '@shared/components/IconsFormer';
import { Sidebar } from '@shared/components/Sidebar';
import { api_ } from '@shared/plugIn/axios';
import { useAuth } from '@shared/state/hooks/useAuth';

import { useSellerReleaseNotes } from '../../hooks/useSellerReleaseNotes';

// const { Delivery, Flower, Modify, Receipt, Home, Question, Setting } = Icon();

//* 면세계산서/계좌환불 처리 대기 건수 — _id만 select하는 초경량 조회라 사실상 count 비용
//* cashReceipt(현금영수증 발행 대기/취소 대기)만 전용 엔드포인트(admin 전용)에서 total만
interface PendingCounts {
  taxInvoiceRequest: number;
  manualRefund: number;
  cashReceipt: number;
  cashReceiptCancel: number;
}

const navigation = (shopId: string, isAdmin: boolean, pending: PendingCounts) => [
  {
    name: 'Home',
    href: `/flower-seller/shop/${shopId}`,
    icon: IconFormer.Home,
    active: true,
  },
  {
    name: '구매시도자',
    href: `/flower-seller/shop/${shopId}/guest-order-manage`,
    icon: IconFormer.Loading,
    active: true,
  },
  {
    name: '주문배송관리',
    href: `/flower-seller/shop/${shopId}/delivery-manage`,
    icon: IconFormer.DeliveryBlue,
    active: true,
  },
  {
    name: '과거배송검색',
    href: `/flower-seller/shop/${shopId}/past-delivery`,
    icon: IconFormer.MagnifyingGlassRegular,
    active: true,
  },

  {
    name: '부고조회',
    href: `/flower-seller/shop/${shopId}/bugo-search`,
    icon: IconFormer.DPBlue,
    active: true,
  },
  {
    name: '입금대기',
    href: `/flower-seller/shop/${shopId}/waiting-list`,
    icon: IconFormer.Loading,
    active: true,
  },
  {
    // 남긴 CS 응대 메모 전체 목록 (자기 매장 범위는 서버가 걸러 준다)
    name: 'CS 응대 로그',
    href: `/flower-seller/shop/${shopId}/cs-memo-log`,
    icon: IconFormer.View,
    active: true,
  },
  {
    name: `면세계산서요청${
      pending.taxInvoiceRequest > 0 ? ` (${pending.taxInvoiceRequest})` : ''
    }`,
    href: `/flower-seller/shop/${shopId}/tax-invoice-requests`,
    icon: IconFormer.Receipt,
    active: true,
  },
  {
    name: `계좌환불요청${pending.manualRefund > 0 ? ` (${pending.manualRefund})` : ''}`,
    href: `/flower-seller/shop/${shopId}/manual-refund-requests`,
    icon: IconFormer.ModifyBlue,
    active: true,
  },
  {
    //* 계좌 입금 건은 PG가 대신 발행해주지 않아 우리가 홈택스에 직접 친다 — 그 대기열.
    //* 한 건 누락돼도 알아챌 방법이 없어서 배지로 남은 건수를 항상 보이게 둔다.
    //* 취소 대기는 "돈은 돌려주고 매출은 남은" 상태라 0이 아니면 따로 붙여 눈에 띄게.
    name: `현금영수증${
      pending.cashReceipt > 0 || pending.cashReceiptCancel > 0
        ? ` (${pending.cashReceipt}${
            pending.cashReceiptCancel > 0 ? `·취소 ${pending.cashReceiptCancel}` : ''
          })`
        : ''
    }`,
    href: `/flower-seller/shop/${shopId}/cash-receipt`,
    icon: IconFormer.Receipt,
    active: isAdmin,
  },
  {
    name: isAdmin ? '도매상품메뉴관리' : '상품메뉴관리',
    href: `/flower-seller/shop/${shopId}/shop-item-info`,
    icon: IconFormer.Flower,
    active: true,
  },
  {
    name: '소매상품메뉴관리',
    href: `/flower-seller/shop/${shopId}/shop-item`,
    icon: IconFormer.Flower,
    active: isAdmin,
  },
  {
    name: '정산',
    href: `/flower-seller/shop/${shopId}/account`,
    icon: IconFormer.ModifyBlue,
    active: true,
  },
  {
    name: '관리자설정',
    href: `/flower-seller/shop/${shopId}/account-settings`,
    icon: IconFormer.Setting,
    active: isAdmin,
  },
  {
    name: '판매자 정보',
    href: `/flower-seller/shop/${shopId}/info`,
    icon: IconFormer.UserBlue,
    active: true,
  },
];
const secondaryNavigation = (shopId: string, hasUnreadReleaseNote: boolean) => [
  {
    //* 화환팀 채널 패치노트 — 안 읽은 배포가 있으면 이름 옆에 점을 찍는다
    //* (읽음 여부는 기기별 localStorage, docs/releases/README.md Phase 1.5)
    name: '업데이트',
    href: `/flower-seller/shop/${shopId}/release-notes`,
    icon: IconFormer.BugoMessageBlue,
    badge: hasUnreadReleaseNote ? (
      <span className="ml-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
    ) : null,
  },
  {
    name: '계정정보',
    href: `/flower-seller/shop/${shopId}/settings`,
    icon: IconFormer.Setting,
  },
];

interface Props {
  shopId: string;
}

export const SellerSidebar: FC<Props> = function SellerSidebar({ shopId }: Props) {
  const { isAdmin, sellerRole } = useAuth();
  //* 안 읽은 화환팀 업데이트가 있으면 메뉴에 점을 찍는다 (번들에 심긴 노트라 조회 없음)
  const { hasUnread: hasUnreadReleaseNote } = useSellerReleaseNotes();

  //* 처리 대기 건수 배지 — 사이드바 마운트 시 1회 조회. seller는 owningFilter로 자기
  //* shop만, admin은 전체 대기열 건수. 구서버/실패면 조용히 0 (배지 없음).
  const [pending, setPending] = useState<PendingCounts>({
    taxInvoiceRequest: 0,
    manualRefund: 0,
    cashReceipt: 0,
    cashReceiptCancel: 0,
  });
  //* role 문자열(안정적 enum 값)에만 의존. sellerRole 함수는 매 렌더 새 참조라
  //* 의존성에 두면 setPending 리렌더 → effect 재실행 → 재조회 무한루프가 됨.
  const role = sellerRole();
  //* isAdmin은 매 렌더 새 참조인 함수라 결과(boolean)만 의존성에 둔다 — role과 같은 이유
  const admin = isAdmin();
  useEffect(() => {
    let alive = true;
    (['taxInvoiceRequest', 'manualRefund'] as const).forEach((kind) => {
      api_
        .get<{ _id: string }[]>(shopOrderSellerRequestPendingCountUrl(kind, role))
        .then(({ data }) => {
          if (alive && Array.isArray(data)) {
            setPending((prev) => ({ ...prev, [kind]: data.length }));
          }
        })
        .catch(() => undefined);
    });
    //* 현금영수증 큐는 admin 전용 엔드포인트 — 셀러가 부르면 401/403이라 아예 안 부른다.
    //* 목록은 필요 없고 총건수만 쓰므로 limit=1로 페이지를 최소화한다.
    if (admin) {
      getShopOrderCashReceiptPending({ limit: 1 })
        .then((data) => {
          if (alive) setPending((prev) => ({ ...prev, cashReceipt: data.total ?? 0 }));
        })
        .catch(() => undefined);
      getShopOrderCashReceiptCancelPending({ limit: 1 })
        .then((data) => {
          if (alive)
            setPending((prev) => ({ ...prev, cashReceiptCancel: data.total ?? 0 }));
        })
        .catch(() => undefined);
    }
    return () => {
      alive = false;
    };
  }, [role, admin]);

  return (
    <Sidebar
      navigation={navigation(shopId, admin, pending)}
      secondaryNavigation={secondaryNavigation(shopId, hasUnreadReleaseNote)}
    />
  );
};
